import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { steps } from "../data/mock";
import { Search, FileText, Send } from "lucide-react";

const stepIcons = [Search, FileText, Send];

export const HowItWorks = () => {
  const [headRef, headVisible] = useScrollAnimation();

  return (
    <section id="how-it-works" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      {/* Animated decorative orb */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-indigo-100/20 rounded-full blur-[120px] pointer-events-none orb-float-3" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center max-w-2xl mx-auto transition-all duration-700 ease-out ${
            headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-indigo-600">
            How It Works
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Three Steps to Winning Bids
          </h2>
          <p className="mt-4 text-lg text-slate-400 leading-relaxed font-body">
            A streamlined process that takes you from discovery to submission.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-20">
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-[3.5rem] left-[16%] right-[16%] h-px bg-gradient-to-r from-indigo-200 via-indigo-300 to-indigo-200" />

            {steps.map((step, i) => (
              <StepItem key={step.id} step={step} index={i} Icon={stepIcons[i]} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const StepItem = ({ step, index, Icon }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-95"
      }`}
      style={{ transitionDelay: `${index * 250}ms` }}
    >
      {/* Step circle */}
      <div className={`relative z-10 w-[4.5rem] h-[4.5rem] rounded-full flex items-center justify-center mb-8 transition-all duration-500 ${
        isVisible ? "shadow-lg shadow-indigo-100/60" : "shadow-none"
      } bg-white/80 backdrop-blur-sm border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-xl hover:shadow-indigo-100/80 hover:scale-110`}>
        <Icon size={24} className="text-indigo-600" />
      </div>

      {/* Number */}
      <span className="text-xs font-bold text-indigo-400 tracking-wider mb-2">
        STEP {step.number}
      </span>

      {/* Title */}
      <h3 className="text-xl font-bold text-slate-900 mb-3">
        {step.title}
      </h3>

      {/* Description */}
      <p className="text-sm text-slate-500 leading-relaxed max-w-xs font-body">
        {step.description}
      </p>
    </div>
  );
};
